import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Chip,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const MIN_AMOUNT = 0.0001;
const MAX_DIGITS_TOTAL = 15;
const MAX_DECIMAL_PLACES = 6;

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
  surface: "#1e1e1e",
  surfaceAlt: "#2a2a2a",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
  surface: "#f8f9fa",
  surfaceAlt: "#eef2f7",
};

// Token type options per currency. `label` is what the user sees in the UI.
const TOKEN_OPTIONS = {
  USDT: [
    { value: "erc", label: "ERC" },
    { value: "trc", label: "TRC" },
    { value: "pol", label: "POL" },
    { value: "bep", label: "BSC" }, // BEP shown as BSC in UI
  ],
  HCX: [
    { value: "NATIVE", label: "NATIVE" },
    { value: "erc", label: "ERC" },
  ],
};

// Returns the fixed token type for currencies that don't need user selection.
const getFixedTokenType = (code) => {
  if ((code || "").toUpperCase() === "USDC") return "erc";
  return "NATIVE";
};

/**
 * PabtekPaymentPopup
 *
 * Props:
 *   open         - boolean
 *   onClose      - function
 *   onProceed    - function({ amount, currencyId, currencyCode, usdEquivalent, tokenType })
 *   currencyId   - number | string
 *   currencyCode - string  e.g. "CO2COIN"
 *   userUuid     - string
 *   theme        - "light" | "dark"
 */
const PabtekPaymentPopup = ({
  open,
  onClose,
  onProceed,
  currencyId,
  currencyCode,
  userUuid = "",
  theme = "light",
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const colors = theme === "dark" ? darkColors : lightColors;
  const isDark = theme === "dark";

  // ── Amount input ─────────────────────────────────────────
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");

  // ── Market price ─────────────────────────────────────────
  const [unitRate, setUnitRate] = useState(null); // raw marketPrice from API: 1 unit crypto = unitRate USD
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");

  // ── Token type ───────────────────────────────────────────
  const tokenOptions = TOKEN_OPTIONS[(currencyCode || "").toUpperCase()] ?? null; // null → no selector
  const defaultTokenType = tokenOptions ? tokenOptions[0].value : getFixedTokenType(currencyCode);
  const [tokenType, setTokenType] = useState(defaultTokenType);

  // ── Payment link ─────────────────────────────────────────
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // ── Derived values ────────────────────────────────────────
  const parsedAmount = parseFloat(amount);
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount >= MIN_AMOUNT;
  const usdEquivalent =
    isAmountValid && unitRate !== null ? parsedAmount * unitRate : null;

  const canProceed =
    isAmountValid &&
    !amountError &&
    unitRate !== null &&
    !priceLoading &&
    !paymentLoading;

  // ── Fetch market price on open ────────────────────────────
  useEffect(() => {
    if (!open) return;

    // Reset all state
    setAmount("");
    setAmountError("");
    setUnitRate(null);
    setPriceError("");
    setPaymentError("");
    setTokenType(
      TOKEN_OPTIONS[(currencyCode || "").toUpperCase()]
        ? TOKEN_OPTIONS[(currencyCode || "").toUpperCase()][0].value
        : getFixedTokenType(currencyCode)
    );

    if (!currencyCode) return;

    setPriceLoading(true);
    fetch("https://accounts.paybito.com/api/home/marketPrice", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currency: currencyCode,
        baseCurrency: "USD",
        action: 1,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const mp = data?.marketPrice;
        if (data?.error?.error_data === 0 && mp && mp > 0) {
          // marketPrice = how many USD 1 unit of the currency is worth
          setUnitRate(mp);
        } else {
          setPriceError(data?.error?.error_msg || "Failed to fetch market price.");
        }
      })
      .catch(() => setPriceError("Failed to fetch market price."))
      .finally(() => setPriceLoading(false));
  }, [open, currencyCode]);

  // ── Validation ────────────────────────────────────────────
  const validateAmount = (val) => {
    if (!val) {
      setAmountError("Amount is required.");
      return;
    }

    const dotIndex = val.indexOf(".");
    const intPart = dotIndex >= 0 ? val.slice(0, dotIndex) : val;
    const decPart = dotIndex >= 0 ? val.slice(dotIndex + 1) : "";
    const totalDigits = intPart.length + decPart.length;

    if (decPart.length > MAX_DECIMAL_PLACES) {
      setAmountError(`Amount allows up to ${MAX_DECIMAL_PLACES} decimal places.`);
      return;
    }

    if (totalDigits > MAX_DIGITS_TOTAL) {
      setAmountError(`Amount allows up to ${MAX_DIGITS_TOTAL} digits in total.`);
      return;
    }

    const num = parseFloat(val);
    if (isNaN(num) || num < MIN_AMOUNT) {
      setAmountError(`Amount must be at least ${MIN_AMOUNT}.`);
    } else {
      setAmountError("");
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;

    if (val) {
      const dotIndex = val.indexOf(".");
      const intPart = dotIndex >= 0 ? val.slice(0, dotIndex) : val;
      const decPart = dotIndex >= 0 ? val.slice(dotIndex + 1) : "";
      const totalDigits = intPart.length + decPart.length;

      // Block input that already violates the hard limits
      if (decPart.length > MAX_DECIMAL_PLACES || totalDigits > MAX_DIGITS_TOTAL) {
        return;
      }
    }

    setAmount(val);
    validateAmount(val);
  };

  // ── Proceed: call generatePabtekPaymentLink ───────────────
  const handleProceed = async () => {
    if (!canProceed) return;
    setPaymentLoading(true);
    setPaymentError("");

    try {
      const res = await fetch(
        "https://accounts.paybito.com/api/transaction/generatePabtekPaymentLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            userUuid: userUuid || "",
            baseCurrencyId: "1",
            amount: String(unitRate != null && parsedAmount > 0 ? parsedAmount * unitRate : 0), // counterAmount * marketPrice
            counterCurrencyId: String(currencyId ?? ""),
            counterAmount: String(parsedAmount),      // user input (crypto amount)
            marketPrice: String(unitRate ?? ""),      // raw value from API
            tokenType,
          }),
        }
      );

      const data = await res.json();

      if (data?.paymentLink) {
        window.open(data.paymentLink, "_blank", "noopener,noreferrer");
        onClose();
        if (typeof onProceed === "function") {
          onProceed({ amount: parsedAmount, currencyId, currencyCode, usdEquivalent, tokenType });
        }
      } else {
        setPaymentError(
          data?.error?.error_msg || "Failed to generate payment link. Please try again."
        );
      }
    } catch {
      setPaymentError("Failed to generate payment link. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(currencyCode || "").toUpperCase()}.png`;

  // ── Render ────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Gradient header band ─────────────────────────────── */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)"
            : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 4,
          position: "relative",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Buy with Card
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
              Secure card payment
            </Typography>
          </Box>
        </Box>

        {/* Currency badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={logoUrl}
            alt={currencyCode}
            sx={{ width: 40, height: 40, bgcolor: "rgba(255,255,255,0.15)" }}
          >
            {(currencyCode || "?")[0]}
          </Avatar>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
              {currencyCode || "—"}
            </Typography>
            {priceLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={10} sx={{ color: "rgba(255,255,255,0.6)" }} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Fetching price…
                </Typography>
              </Box>
            ) : priceError ? (
              <Typography variant="caption" sx={{ color: "#ff8a80" }}>
                Price unavailable
              </Typography>
            ) : unitRate !== null ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                1 {currencyCode} = ${unitRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} USD
              </Typography>
            ) : null}
          </Box>

          {/* Market rate chip */}
          {unitRate !== null && !priceLoading && (
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: "14px !important", color: "#4caf50 !important" }} />}
              label="Live Rate"
              size="small"
              sx={{
                ml: "auto",
                backgroundColor: "rgba(76,175,80,0.2)",
                color: "#81c784",
                border: "1px solid rgba(76,175,80,0.3)",
                fontWeight: 600,
                fontSize: "0.65rem",
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Content ─────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* Amount input */}
          <Box>
            <TextField
              placeholder={`0.0000`}
              type="number"
              value={amount}
              onChange={handleAmountChange}
              error={!!amountError}
              helperText={amountError || `Min: ${MIN_AMOUNT} ${currencyCode || ""}`}
              fullWidth
              inputProps={{ min: MIN_AMOUNT, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        pl: 1.5,
                        borderLeft: `1px solid ${colors.border}`,
                      }}
                    >
                      <Avatar
                        src={logoUrl}
                        alt={currencyCode}
                        sx={{ width: 20, height: 20, bgcolor: colors.primary + "33" }}
                      >
                        {(currencyCode || "?")[0]}
                      </Avatar>
                      <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: "0.9rem" }}>
                        {currencyCode}
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceAlt,
                  color: colors.text,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  "& fieldset": { borderColor: colors.border },
                  "&:hover fieldset": { borderColor: colors.primary },
                  "&.Mui-focused fieldset": { borderColor: colors.primary, borderWidth: 2 },
                },
                "& .MuiOutlinedInput-input": { py: 1.75 },
                "& .MuiInputLabel-root": { color: colors.textSecondary },
                "& .MuiFormHelperText-root": {
                  color: amountError ? "error.main" : colors.textSecondary,
                  mt: 0.5,
                },
              }}
            />
          </Box>

          {/* USD equivalent live preview */}
          {usdEquivalent !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(144,202,249,0.08)" : "rgba(25,118,210,0.06)",
                border: `1px solid ${isDark ? "rgba(144,202,249,0.2)" : "rgba(25,118,210,0.15)"}`,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                You Pay
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.primary, fontWeight: 700, fontSize: "1rem" }}
              >
                ≈ ${usdEquivalent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
              </Typography>
            </Box>
          )}

          {/* Network / Token type selector — only for USDT and HCX */}
          {tokenOptions && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75, display: "block" }}
              >
                Select Network
              </Typography>
              <ToggleButtonGroup
                value={tokenType}
                exclusive
                onChange={(_, val) => { if (val) setTokenType(val); }}
                size="small"
                fullWidth
                sx={{ gap: 1 }}
              >
                {tokenOptions.map((opt) => (
                  <ToggleButton
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      flex: 1,
                      borderRadius: "10px !important",
                      border: `1px solid ${colors.border} !important`,
                      color: colors.textSecondary,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      py: 1,
                      transition: "all 0.15s ease",
                      "&.Mui-selected": {
                        backgroundColor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.1)",
                        color: colors.primary,
                        borderColor: `${colors.primary} !important`,
                        borderWidth: "2px !important",
                      },
                      "&:hover": {
                        backgroundColor: isDark ? "rgba(144,202,249,0.08)" : "rgba(25,118,210,0.06)",
                      },
                    }}
                  >
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Price unavailable error */}
          {priceError && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(255,167,38,0.1)" : undefined,
                color: isDark ? "#ffb74d" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ffb74d" : undefined },
              }}
            >
              {priceError}
            </Alert>
          )}

          {/* Payment link error */}
          {paymentError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(229,115,115,0.1)" : undefined,
                color: isDark ? "#ef9a9a" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ef9a9a" : undefined },
              }}
            >
              {paymentError}
            </Alert>
          )}
        </Box>
      </DialogContent>

      {/* ── Actions ─────────────────────────────────────────── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
        <Divider sx={{ borderColor: colors.border, mb: 2.5 }} />

        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={!canProceed}
          fullWidth
          endIcon={
            paymentLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <OpenInNewIcon fontSize="small" />
            )
          }
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
            background: canProceed
              ? "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)"
              : undefined,
            boxShadow: canProceed
              ? "0 4px 16px rgba(25,118,210,0.35)"
              : "none",
            "&:hover": {
              background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
              boxShadow: "0 6px 20px rgba(25,118,210,0.45)",
            },
            "&.Mui-disabled": {
              backgroundColor: isDark ? "#2a2a2a" : "#e0e0e0",
              color: isDark ? "#555" : "#9e9e9e",
            },
            mb: 1.5,
          }}
        >
          {paymentLoading ? "Generating Payment Link…" : "Proceed to Payment"}
        </Button>

        <Button
          onClick={onClose}
          disabled={paymentLoading}
          fullWidth
          sx={{
            borderRadius: "12px",
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.9rem",
            textTransform: "none",
            color: colors.textSecondary,
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              color: colors.text,
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};

export default PabtekPaymentPopup;
